// "+ Create" of the Scripts IDE (spec/dashboard.md section 10.8): the name validated live with the rule's words, the group
// (existing or new), the description; the server writes the generated template (POST without `source`).
import { useMemo, useState } from 'react';
import { ApiError } from '../../api/client';
import { ops } from '../../api/operations';
import type { components } from '../../api/schema';
import { useAuth } from '../../auth/AuthContext';
import { useGame } from '../../shell/GameContext';
import { Button } from '../../ui/Button';
import { Field } from '../../ui/Field';
import { Select, TextArea, TextInput } from '../../ui/inputs';
import { AlertCircle } from '../../ui/icons';
import { Modal } from '../../ui/Modal';

export type ExtensionDto = components['schemas']['ExtensionDto'];

export const NAME_RULE = /^[A-Z][A-Za-z0-9_]*$/;
export const NAME_MESSAGE = 'Only english letters (first symbol should be capital), underscore and digits allowed for Name';
const NEW_GROUP = '\u0000new';

export function CreateScriptDialog({ branch, groups, existing, onClose, onCreated }: { branch: string; groups: string[]; existing: string[]; onClose: () => void; onCreated: (dto: ExtensionDto) => void }) {
  const { client } = useAuth();
  const { org, game } = useGame();
  const [name, setName] = useState('');
  const [touched, setTouched] = useState(false);
  const [groupChoice, setGroupChoice] = useState<string>(groups[0] ?? NEW_GROUP);
  const [newGroup, setNewGroup] = useState('');
  const [description, setDescription] = useState('');
  const [busy, setBusy] = useState(false);
  const [serverError, setServerError] = useState<{ name?: string; group?: string; general?: string }>({});

  const nameError = useMemo(() => {
    if (!touched && name === '') return null;
    if (name === '') return 'Name is required.';
    if (!NAME_RULE.test(name)) return NAME_MESSAGE;
    if (existing.includes(name)) return `An extension named ${name} exists already.`;
    return null;
  }, [name, touched, existing]);
  const group = groupChoice === NEW_GROUP ? newGroup.trim() : groupChoice;
  const valid = name !== '' && nameError === null;

  const submit = async () => {
    setTouched(true);
    if (!valid) return;
    setBusy(true);
    setServerError({});
    try {
      const dto = await client.data(ops.createExtension, {
        path: { org, game: game.slug, branch },
        body: { name, group: group || null, description: description.trim() || null, enabled: null, flags: null, source: null, message: null },
      });
      onCreated(dto);
    } catch (e) {
      if (e instanceof ApiError) {
        setServerError({ name: e.fieldMessage('name') ?? (e.status === 409 ? `An extension named ${name} exists already.` : undefined), group: e.fieldMessage('group'), general: e.fieldMessage('name') || e.fieldMessage('group') || e.status === 409 ? undefined : e.message });
      } else setServerError({ general: e instanceof Error ? e.message : String(e) });
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal
      title="New script"
      open
      onClose={onClose}
      size="m"
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button variant="primary" busy={busy} disabled={!valid} onClick={() => void submit()}>
            Create
          </Button>
        </>
      }
    >
      <form
        data-testid="create-script"
        className="scripts-create form-grid"
        onSubmit={(e) => {
          e.preventDefault();
          void submit();
        }}
      >
        <Field className="span-2" label="Name" required error={nameError ?? serverError.name} help="The class name of the extension.">
          <TextInput
            className="mono"
            value={name}
            autoComplete="off"
            spellCheck={false}
            onChange={(e) => {
              setName(e.target.value);
              setTouched(true);
              setServerError((s) => ({ ...s, name: undefined }));
            }}
          />
        </Field>
        <Field label="Group" error={serverError.group}>
          <Select value={groupChoice} onChange={(e) => setGroupChoice(e.target.value)}>
            {groups.map((g) => (
              <option key={g} value={g}>
                {g}
              </option>
            ))}
            <option value={NEW_GROUP}>New group…</option>
          </Select>
        </Field>
        {groupChoice === NEW_GROUP && (
          <Field label="New group" help="Leave empty for the default group.">
            <TextInput value={newGroup} onChange={(e) => setNewGroup(e.target.value)} />
          </Field>
        )}
        <Field className="span-2" label="Description">
          <TextArea value={description} rows={3} onChange={(e) => setDescription(e.target.value)} />
        </Field>
        {serverError.general && (
          <div className="scripts-alert span-2" role="alert">
            <AlertCircle className="scripts-alert-icon" aria-hidden="true" />
            <span>{serverError.general}</span>
          </div>
        )}
        <button type="submit" hidden />
      </form>
    </Modal>
  );
}
